<script setup lang="ts">
  import type { FareStoryblok, FareItemStoryblok } from '@/storyblok/component-types-sb'
  import { cva } from 'class-variance-authority'
  import type { ISbStoryData } from '@storyblok/vue'
  import type { TFareCategory } from '@/storyblok/types'

  const props = defineProps<{
    blok: ISbStoryData<FareStoryblok>
    category: TFareCategory
  }>()

  const recommendedStyleKey = computed(() => {
    return props.category === 'economy' ? 'economy' : 'business'
  })

  const containerBase = computed(() => {
    return cva('relative rounded-md py-6 px-4 h-200 md:h-auto border-2', {
      variants: {
        recommended: {
          economy: 'border-bt-green',
          business: 'border-bt-blue-dark'
        }
      }
    })({
      recommended: props.blok.content.recommended ? recommendedStyleKey.value : null
    })
  })

  const recommendedFlagColorVariant = computed(() => {
    return props.category === 'economy' ? 'greenGradient' : 'darkBlueGradient'
  })

  const fareItems = computed((): ISbStoryData<FareItemStoryblok>[] => {
    const items = props.blok.content.items
    if (!Array.isArray(items)) return []

    return items.filter(
      (item): item is ISbStoryData<FareItemStoryblok> =>
        typeof item !== 'string' && item !== null && typeof item === 'object' && 'content' in item
    )
  })

  const { t } = useI18n()

  const recommendedText = computed(() => {
    return props.category === 'economy' ? t('fareTypes.recommended') : t('fareTypes.smart-choice')
  })
</script>
<template>
  <div class="pt-7">
    <div :class="containerBase">
      <UiLabel
        v-if="blok.content.recommended"
        class="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2"
        :text="recommendedText"
        :color-variant="recommendedFlagColorVariant"
        variant="bestValue"
      />
      <h5 class="m-0 w-full text-center">{{ blok.content.title }}</h5>
      <p class="mb-0 mt-2 w-full text-center text-secondary-light text-body-2">{{ blok.content.subtext }}</p>
      <div class="mt-6 flex flex-col gap-4">
        <div
          v-for="item in fareItems"
          :key="item.content._uid"
        >
          <div class="flex flex-row items-start gap-2">
            <NuxtImg
              v-if="item.content.icon?.filename"
              :src="item.content.icon.filename"
              :alt="item.content.icon.alt ?? ''"
              height="24"
              width="24"
              class="shrink-0"
            />
            <StoryblokComponent
              v-for="bodyItem in item.content.body"
              :key="bodyItem._uid"
              :blok="bodyItem"
              class="[&>div]:mt-0"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
